import {
  zApiError,
  zGameCapacity,
  zGameSummary,
  zIntelReport,
  zJoinResponse,
  zSeatClaimResponse,
  zSeatReleaseResponse,
  type GameCapacity,
  type GameSummary,
  type IntelReportWire,
  type JoinResponse,
  type PhaseState,
  type SeatClaimResponse,
  type SeatReleaseResponse,
} from '@badger-sentinel/protocol';

import { apiUrl } from './config.js';
import { httpFetch } from './transport.js';

/**
 * REST calls for join, seat claim, and the GM controls.
 *
 * Every response is **parsed** with the shared schema, never cast (§19). A server
 * that has been redeployed with a wider shape than this client understands is a
 * real scenario — that is what the version endpoint is for — and the failure
 * should be a clear message rather than an undefined field three renders later.
 */

export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
    message: string,
    readonly correlationId?: string,
    /**
     * What the join bucket has left on this key, and how long a full one holds
     * — read off `x-ratelimit-remaining` / `retry-after`, which the API
     * exposes cross-origin for exactly this (`app.ts`, CORS `exposedHeaders`).
     * Entry prints the first as ATTEMPTS LEFT, which the copy rules allow
     * because it is a number the player needs now.
     */
    readonly attemptsLeft?: number,
    readonly retryAfterS?: number,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

/** A header that should be a non-negative integer, or nothing. */
function headerCount(response: Response, name: string): number | undefined {
  let raw: string | null = null;
  try {
    // A stubbed `fetch` in a test carries no `headers`, and reading one is not
    // worth turning a well-formed refusal into an unhandled TypeError.
    raw = response.headers.get(name);
  } catch {
    return undefined;
  }
  if (raw === null) return undefined;
  const value = Number(raw);
  return Number.isInteger(value) && value >= 0 ? value : undefined;
}

async function call<T>(url: string, init: RequestInit, parse: (value: unknown) => T): Promise<T> {
  let response: Response;
  try {
    response = await httpFetch(url, {
      ...init,
      headers: { 'content-type': 'application/json', ...(init.headers ?? {}) },
    });
  } catch {
    // `fetch` rejects with an opaque TypeError and the browser withholds the
    // reason, so this must not name one. It used to say "usually a CORS or
    // mixed-content problem" and carry the exception's own text — which was
    // wrong most of the time it fired (a client built with no
    // `VITE_API_BASE_URL` lands here), and sent a cadet with a broken Wi-Fi
    // connection to go and read about CORS. The diagnostics panel is where a
    // deploy gets debugged; this is what a player reads.
    throw new ApiError(0, 'NETWORK', 'Check the connection, then try again.');
  }

  const body: unknown = await response.json().catch(() => null);
  if (!response.ok) {
    const parsed = zApiError.safeParse(body);
    throw new ApiError(
      response.status,
      parsed.success ? parsed.data.code : 'INTERNAL',
      parsed.success ? parsed.data.message : `Request failed (${response.status}).`,
      parsed.success ? parsed.data.correlationId : undefined,
      headerCount(response, 'x-ratelimit-remaining'),
      headerCount(response, 'retry-after'),
    );
  }

  // A body that does not parse is a server this bundle does not understand —
  // §19's whole point. It leaves through `ApiError` like every other failure,
  // so no caller has to render a stringified ZodError to a player.
  try {
    return parse(body);
  } catch {
    throw new ApiError(response.status, 'INTERNAL', 'Reload the page and try again.');
  }
}

/**
 * Seats free per side, before any credential (`GameCapacity`).
 *
 * The only unauthenticated read in the app. A 404 means the code is wrong —
 * which is the one thing this endpoint discloses that join does not, and is
 * discussed on the schema.
 */
export async function fetchCapacity(
  baseUrl: string,
  code: string,
  signal?: AbortSignal,
): Promise<GameCapacity> {
  return call(
    apiUrl(baseUrl, `/api/games/${encodeURIComponent(code)}/capacity`),
    { method: 'GET', ...(signal ? { signal } : {}) },
    (value) => zGameCapacity.parse(value),
  );
}

export async function joinGame(
  baseUrl: string,
  code: string,
  role: 'BLUFOR' | 'REDFOR' | 'GM',
  password: string,
  displayName?: string,
): Promise<JoinResponse> {
  return call(
    apiUrl(baseUrl, `/api/games/${encodeURIComponent(code)}/join`),
    {
      method: 'POST',
      body: JSON.stringify({ role, password, ...(displayName ? { displayName } : {}) }),
    },
    (value) => zJoinResponse.parse(value),
  );
}

export async function claimSeat(
  baseUrl: string,
  gameId: string,
  sessionToken: string,
  cell: string,
  options: { displayName?: string; reclaimToken?: string } = {},
): Promise<SeatClaimResponse> {
  // Both fields are optional on the wire and the server reuses the join-time
  // name when `displayName` is absent — so send the key only when it has a
  // value, never `undefined`, which `strict()` would reject as an unknown null.
  const body: Record<string, string> = {};
  if (options.displayName) body.displayName = options.displayName;
  if (options.reclaimToken) body.reclaimToken = options.reclaimToken;

  return call(
    apiUrl(baseUrl, `/api/games/${gameId}/seats/${cell}/claim`),
    {
      method: 'POST',
      headers: { authorization: `Bearer ${sessionToken}` },
      body: JSON.stringify(body),
    },
    (value) => zSeatClaimResponse.parse(value),
  );
}

/** Self-service release (shell.md Open: "releasing a seat deliberately"). */
export async function releaseSeat(
  baseUrl: string,
  gameId: string,
  seatToken: string,
  cell: string,
): Promise<SeatReleaseResponse> {
  return call(
    apiUrl(baseUrl, `/api/games/${gameId}/seats/${cell}/release`),
    { method: 'POST', headers: { authorization: `Bearer ${seatToken}` } },
    (value) => zSeatReleaseResponse.parse(value),
  );
}

export async function fetchGame(
  baseUrl: string,
  gameId: string,
  sessionToken: string,
): Promise<GameSummary> {
  return call(
    apiUrl(baseUrl, `/api/games/${gameId}`),
    { method: 'GET', headers: { authorization: `Bearer ${sessionToken}` } },
    (value) => zGameSummary.parse(value),
  );
}

export async function gmControl(
  baseUrl: string,
  gameId: string,
  seatToken: string,
  action: string,
  extendMs?: number,
): Promise<PhaseState> {
  return call(
    apiUrl(baseUrl, `/api/games/${gameId}/control`),
    {
      method: 'POST',
      headers: { authorization: `Bearer ${seatToken}` },
      body: JSON.stringify({ action, ...(extendMs ? { extendMs } : {}) }),
    },
    (value) => value as PhaseState,
  );
}

/**
 * One resolved turn's intel report, for the seat holding this token (M5 Q18).
 *
 * The only REST read in the app that returns game data, and it is deliberately
 * REST rather than a socket message type: an archive pull is initiated by an
 * operator scrolling a rail, it paginates by turn, and it has no business in the
 * replay buffer, which exists so a reconnecting seat catches up on what it
 * *missed*. The live report still arrives on the socket.
 *
 * Parsed with `zIntelReport`, like every other response — a fetched report is
 * projected for the requesting seat on every request, and the parse is what makes
 * an archive entry the same shape, through the same schema, as the live one.
 *
 * The three refusals are the caller's to tell apart: 404 is "that turn has not
 * resolved yet", 403 is the umpire (`buildIntelReport` takes a player viewer, and
 * the GM's log is M6's console), and anything else is the link.
 */
export async function fetchIntelReport(
  baseUrl: string,
  gameId: string,
  seatToken: string,
  turn: number,
  signal?: AbortSignal,
): Promise<IntelReportWire> {
  return call(
    apiUrl(baseUrl, `/api/games/${gameId}/intel/${String(turn)}`),
    {
      method: 'GET',
      headers: { authorization: `Bearer ${seatToken}` },
      ...(signal ? { signal } : {}),
    },
    (value) => zIntelReport.parse(value),
  );
}
